import { useState, useMemo, useCallback, useContext } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { capitalise } from 'lib/string/stringUtils'
import ModalContext from 'contexts/ModalContext'
import AddBookingModal from 'tripPlanner/components/Chat/AddBookingModal'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import { useChatFiles } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatFilesContext'
import useTypingAnimation from 'hooks/useTypingAnimation'
import { mediaQueryDown } from 'components/utils/breakpoint'

const TYPING_OPTIONS = {
  typingInterval: 50,
  pausingMs: 2000,
  deletingTime: 20,
}

const GlowingInputWrapper = styled.div`
  width: 100%;
  border-radius: ${rem(24)};
  animation: glow 4s ease-in-out infinite;

  textarea::placeholder {
    font-weight: ${props => props.theme.font.primary.weight.bold};
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 ${rem(10)} 0 ${props => props.theme.palette.highlight.primary.lightForeground};
    }
    50% {
      box-shadow: 0 0 ${rem(40)} ${rem(15)} ${props => props.theme.palette.highlight.primary.lightForeground};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${mediaQueryDown.mobile} {
    border-radius: ${rem(24)};
  }
`

const HeroContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(32)};
  width: 100%;

  ${mediaQueryDown.mobile} {
    gap: ${rem(28)};
  }
`

const HeroHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  text-align: start;

  ${mediaQueryDown.mobile} {
    gap: ${rem(16)};
  }
`

interface Props {
  onSubmit: (message: string) => void
  disabled?: boolean
  selectedTags?: Array<string>
  selectedBookingItemIds?: Array<string>
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  onRemoveTag?: (tag: string) => void
  showAddBookingAction?: boolean
  className?: string
}

function ChatLandingHero({
  onSubmit,
  disabled = false,
  selectedTags,
  selectedBookingItemIds,
  onBookingItemsAdded,
  onRemoveTag,
  showAddBookingAction = true,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { files, setFiles } = useChatFiles()

  const handleAddBookingClick = useCallback(() => {
    showModal(
      <AddBookingModal
        onSendMessage={onSubmit}
        disabledBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={onBookingItemsAdded}
        isSubmitLocked={disabled}
      />,
    )
  }, [disabled, onBookingItemsAdded, onSubmit, selectedBookingItemIds, showModal])
  const account = useAppSelector(state => state.auth.account)
  const userName = account.givenName ? capitalise(account.givenName) : ''
  const [isFocused, setIsFocused] = useState(false)

  const placeholderTexts = useMemo(() => [
    intl.formatMessage({
      id: 'xRhEq9TSmshW5PPir61H',
      defaultMessage: 'Try: "Create a 5 day trip to Bali for my family"',
      description: 'Rotating placeholder prompt example for the TripPlanner chat input on landing page',
    }),
    intl.formatMessage({
      id: 'sEVbT4JtgpwMdIddJ3tb',
      defaultMessage: 'Try: "Plan a relaxed long weekend in Melbourne"',
      description: 'Rotating placeholder prompt example for weekend trip in TripPlanner chat input',
    }),
    intl.formatMessage({
      id: 'f3Z5FrYBcQ08a792LV4K',
      description: 'Rotating placeholder prompt example for Italy trip in TripPlanner chat input',
      defaultMessage: 'Try: "I’ve got 10 days in Italy — where should I go?"',
    }),
    intl.formatMessage({
      id: 'H0O-gyvyMmH5E1Sxe7uu',
      defaultMessage: 'Try: "Help me plan a honeymoon in Japan"',
      description: 'Rotating placeholder prompt example for honeymoon trip in TripPlanner chat input',
    }),
  ], [intl])

  const typedPlaceholder = useTypingAnimation(placeholderTexts, isFocused, TYPING_OPTIONS)

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const greetingText = useMemo(() => {
    if (userName) {
      return (
        <FormattedMessage
          id="CjL4DA9zxTibheM6abSp"
          defaultMessage="<italic>Hi {userName}</italic>, let’s plan your trip together"
          values={{
            userName,
            italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
          }}
          description="Greeting message with user's name in the trip planner feature"
        />
      )
    }
    return (
      <FormattedMessage
        id="AFG9UfNYMFM_s7n8HAbo"
        defaultMessage="<italic>Hello,</italic> let’s plan your trip together"
        values={{
          italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        }}
        description="Greeting message without user's name in the trip planner feature"
      />
    )
  }, [userName])

  return (
    <HeroContainer className={className}>
      <HeroHeader>
        <Heading variant="heading2">
          {greetingText}
        </Heading>
        <BodyText variant="large" align="start">
          {intl.formatMessage({
            id: 'pgdTxhfSnovXL_MqxpN4',
            defaultMessage: 'Welcome to TripPlanner, your personal travel designer.',
            description: 'First line of the hero description on the chat landing page',
          })}
          <br />
          {intl.formatMessage({
            id: 'GUuXdGHQ2k4hl8yF6vX_',
            defaultMessage: 'To help me craft the perfect itinerary for you, tell me a little more about your plans.',
            description: 'Second line of the hero description on the chat landing page',
          })}
        </BodyText>
      </HeroHeader>

      <GlowingInputWrapper onFocus={handleFocus} onBlur={handleBlur}>
        <ChatMessageInput
          onSubmit={onSubmit}
          disableSubmit={disabled}
          placeholder={typedPlaceholder}
          files={files}
          onFilesChange={setFiles}
          selectedTags={selectedTags}
          onAddBookingClick={handleAddBookingClick}
          showAddBookingAction={showAddBookingAction}
          onRemoveTag={onRemoveTag}
          hideDisclaimer
        />
      </GlowingInputWrapper>
    </HeroContainer>
  )
}

export default ChatLandingHero
